import * as React from "react";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { Gender } from "../../../../../../../Wrap/Constants/Enums/Domain/Gender";
import { QuoteModel } from "../../../../../../../Wrap/Models/Models/Processes/Quote/QuoteModel";
import { SubAccountTypeArrayMap } from "../../../../../../Admin/Company/CompanyWizard/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { SubAccountTypeHelper } from "../../../Common/Helpers/SubAccountTypeHelper";
import { jointAccountParser } from "../../../Common/Review/ReviewParser";
import { QuoteInstructionStore } from "../../Store/Variable";
import { Document } from "./Document";
import { StepReview } from "./StepReview";
import { IllustrationsAvailable } from "../../../Common/Documents/Components/IllustrationsAvailable";
import { TitleMap } from "../../../../../../Shared/Entities/Maps/TitleMap";
import { InstructionDocumentTypeRequestTriggerActionsCreator } from "../../../Common/Documents/Actions/InstructionDocumentTypeRequestTriggerActionsCreator";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";

interface IReviewProps extends IProcessComponentProps {
    data: QuoteModel;
}

interface IReviewState {}

export class Review extends ProcessComponent<IReviewProps, IReviewState> {
    constructor(props: any) {
        super(props);
        this.state = { isDocLoading: false };
    }

    componentDidMount(): void {
        super.componentDidMount();
        InstructionDocumentTypeRequestTriggerActionsCreator.triggerByQuoteModel(this.props.data);
    }

    private successLabelRender() {
        const { data } = this.props;
        const createdDate = JsonDateParser.getFormatDateString(
            JsonDateParser.toDate(data?.QuoteDate),
            "dd MM yyyy",
            true
        );
        const quoteReference = data.QuoteReference;
        const config = [
            {
                title: "Created:",
                data: createdDate,
            },
            {
                title: "Wrapper:",
                data: data.SelectedProducts.map(
                    (ele) => SubAccountTypeArrayMap.optionsMap[ele.SubAccountType]
                ).toString(),
            },
            {
                title: "Quote reference:",
                data: quoteReference?.HasQuoteReference ? quoteReference.QuoteReferenceDetail : "None",
            },
        ];
        return (
            <div>
                <h1>
                    <span className="alert-success">Quote {getQueryParameters()["id"]} successfully created</span>
                </h1>
                <br />
                <h2>Quote No. {getQueryParameters()["id"]}</h2>
                <Typography type="narrow-dt-width" wrapCss="quote-list" config={config}></Typography>
                <IllustrationsAvailable onRef={(ref) => this.appendChild(ref)} onChange={() => {}} data={data} />
            </div>
        );
    }

    private clientDetailRender() {
        const { ClientDetails } = this.props.data;

        const birthDate = JsonDateParser.getFormatDateString(
            JsonDateParser.toDate(ClientDetails?.DateOfBirth),
            "dd MM yyyy",
            true
        );

        const config = [
            {
                title: "Title",
                data: TitleMap.getString(ClientDetails?.Title),
            },
            {
                title: "Name",
                data: `${ClientDetails?.FirstName} ${ClientDetails?.LastName}`,
            },
            {
                title: "Gender",
                data: ClientDetails?.Gender == Gender.Female ? "Female" : "Male",
            },
            {
                title: "Date of birth",
                data: birthDate,
            },
        ];
        return (
            <>
                <Accordion label="Client details" isOpen={true}>
                    <Typography type="narrow-dt-width" config={config} wrapCss="pl-34"></Typography>
                    {this.props.data.IsJointAccount &&
                        this.props.data.SecondClientDetails &&
                        jointAccountParser(this.props.data.ClientDetails, this.props.data.SecondClientDetails)}
                </Accordion>
            </>
        );
    }

    private getStepReviewProps = (key) => {
        return {
            data: this.props.data[key],
            model: this.props.data,
        };
    };

    private getExternalFundDocument(): Array<string> {
        const giaInvestments = this.props.data.GiaQuote.InstrumentSelections?.Instruments;
        const isaInvestments = this.props.data.ISAQuote.InstrumentSelections?.Instruments;
        const pensionInvestments = this.props.data.PensionQuote.InstrumentSelections?.Instruments;
        const instrumentCodes: Array<string> = [];
        giaInvestments &&
            giaInvestments.map((item, index) => {
                instrumentCodes.push(item?.Instrument?.ProductCode);
            });
        isaInvestments &&
            isaInvestments.map((item, index) => {
                instrumentCodes.push(item?.Instrument?.ProductCode);
            });
        pensionInvestments &&
            pensionInvestments.map((item, index) => {
                instrumentCodes.push(item?.Instrument?.ProductCode);
            });
        return instrumentCodes;
    }

    render() {
        const selectedProducts = this.props.data.SelectedProducts;
        const pensionDetail = selectedProducts.find((ele) =>
            SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType)
        );
        const isaDetail = selectedProducts.find((ele) => SubAccountTypeHelper.isIsa(ele.SubAccountType));
        const ppDetail = selectedProducts.find((ele) => SubAccountTypeHelper.isPP(ele.SubAccountType));

        return QuoteInstructionStore.isLoading() ? (
            <PartLoading isLoading={QuoteInstructionStore.isLoading()}></PartLoading>
        ) : (
            <div>
                {this.successLabelRender()}
                <Document {...this.props} />
                {this.clientDetailRender()}
                {pensionDetail && (
                    <div className="mb-40">
                        {isaDetail || ppDetail ? <h2>{pensionDetail.SubAccountType == SubAccountType.JuniorSipp ?  "abrdn JSIPP details" : "Aberdeen SIPP details"}</h2> : null}
                        <StepReview
                            {...this.getStepReviewProps("PensionQuote")}
                            subAccountType={pensionDetail.SubAccountType}
                        />
                    </div>
                )}
                {isaDetail && (
                    <div className="mb-40">
                        {pensionDetail || ppDetail ? <h2>ISA details</h2> : null}
                        <StepReview
                            {...this.getStepReviewProps("ISAQuote")}
                            subAccountType={isaDetail.SubAccountType}
                        />
                    </div>
                )}
                {ppDetail && (
                    <>
                        {pensionDetail || isaDetail ? <h2>Personal Portfolio details</h2> : null}
                        <StepReview {...this.getStepReviewProps("GiaQuote")} subAccountType={ppDetail.SubAccountType} />
                    </>
                )}
            </div>
        );
    }
}
